// Drawola: the small welcome-only dictionary index.html carries. Fork-only.
//
// vite.config.mts builds it from the production language list and the app
// locale files (see welcomeBuildData.ts) and writes it into a JSON script tag.
// The inline welcome script and the React welcome both read it, so the page
// shows the chosen language from the first paint, without waiting for the
// full locale file and without a flash of English.

/** Id of the JSON script tag in index.html that holds the dictionary. */
export const WELCOME_DICTIONARY_ID = "drawola-welcome-i18n";

/** Order of the four welcome points, as the page lists them. */
export const WELCOME_POINT_KEYS = [
  "whiteboard",
  "flowchart",
  "collab",
  "drive",
] as const;

export type WelcomeLanguage = {
  code: string;
  /** Native language name, from upstream's language list. */
  label: string;
  rtl?: boolean;
};

export type WelcomeStrings = {
  title: string;
  lead: string;
  cta: string;
  manual: string;
  /** Label of the language picker. */
  language: string;
  points: Record<typeof WELCOME_POINT_KEYS[number], string>;
  /** Collaboration guide link for this language, null when it has none. */
  guideHref: string | null;
};

export type WelcomeDictionary = {
  storageKey: string;
  languages: WelcomeLanguage[];
  strings: Record<string, WelcomeStrings>;
};

let cached: { source: Element; dictionary: WelcomeDictionary | null } | null =
  null;

/**
 * The dictionary index.html ships, or null when the page has none (tests,
 * other entry pages) or it does not parse. Parsed once per script element.
 */
export const readWelcomeDictionary = (
  doc: Document = document,
): WelcomeDictionary | null => {
  const source = doc.getElementById(WELCOME_DICTIONARY_ID);
  if (!source) {
    return null;
  }
  if (cached?.source !== source) {
    let dictionary: WelcomeDictionary | null = null;
    try {
      dictionary = JSON.parse(source.textContent ?? "") as WelcomeDictionary;
    } catch (error) {
      console.warn(
        `Welcome language data is invalid: ${String(
          error instanceof Error ? error.message : error,
        ).slice(0, 160)}`,
      );
    }
    cached = { source, dictionary };
  }
  return cached.dictionary;
};

/** Reads one welcome string such as "points.drive" from a dictionary entry. */
export const welcomeText = (strings: WelcomeStrings, key: string): string => {
  const value = key
    .split(".")
    .reduce<unknown>(
      (node, part) =>
        node && typeof node === "object"
          ? (node as Record<string, unknown>)[part]
          : undefined,
      strings,
    );
  if (typeof value !== "string") {
    throw new Error(`Welcome dictionary has no string for ${key}`);
  }
  return value;
};
